import { useEffect, useMemo, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import { TokenInsertButtons } from './tokenTools';

const API_BASE = process.env.REACT_APP_URL || 'http://localhost:3501';

const escapeAttr = (value) => String(value || '')
  .replace(/&/g, '&amp;')
  .replace(/"/g, '&quot;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;');

export default function TextSectionEditor({ content, update }) {
  const textareaRef = useRef(null);
  const body = content?.body || '';
  const [linkUrl, setLinkUrl] = useState('');
  const [openInNewTab, setOpenInNewTab] = useState(false);
  const [pages, setPages] = useState([]);
  const [pageId, setPageId] = useState('');

  useEffect(() => {
    let mounted = true;

    const loadPages = async () => {
      try {
        const response = await fetch(`${API_BASE}/pagebuilder/pages`, {
          headers: { 'Content-Type': 'application/json' },
        });

        if (!response.ok) return;
        const data = await response.json();
        if (!mounted || !Array.isArray(data)) return;

        setPages(data);
        if (!pageId && data[0]?.id) setPageId(data[0].id);
      } catch {
        // Non-blocking: page links helper simply remains unavailable.
      }
    };

    loadPages();

    return () => {
      mounted = false;
    };
  }, [pageId]);

  const pageOptions = useMemo(
    () => pages.map((p) => ({ id: p.id, label: p.title || p.name || p.id })),
    [pages]
  );

  const applyInsertion = (build) => {
    const el = textareaRef.current;
    if (!el) return;

    const start = typeof el.selectionStart === 'number' ? el.selectionStart : body.length;
    const end = typeof el.selectionEnd === 'number' ? el.selectionEnd : body.length;
    const selected = body.slice(start, end);

    const inserted = build(selected);
    const next = `${body.slice(0, start)}${inserted}${body.slice(end)}`;
    update('body', next);

    const nextCaret = start + inserted.length;
    requestAnimationFrame(() => {
      if (!textareaRef.current) return;
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(nextCaret, nextCaret);
    });
  };

  const wrapInline = (prefix, suffix, placeholder) => {
    applyInsertion((selected) => `${prefix}${selected || placeholder}${suffix}`);
  };

  const wrapBlock = (tag, placeholder, attrs = '') => {
    const attrPart = attrs ? ` ${attrs}` : '';
    wrapInline(`<${tag}${attrPart}>`, `</${tag}>`, placeholder);
  };

  const insertList = (ordered) => {
    applyInsertion((selected) => {
      const lines = (selected || 'List item')
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean);

      const listItems = lines.map((line) => `<li>${line}</li>`).join('');
      return ordered ? `<ol>${listItems}</ol>` : `<ul>${listItems}</ul>`;
    });
  };

  const insertExternalLink = () => {
    if (!linkUrl.trim()) return;

    const safeHref = escapeAttr(linkUrl.trim());
    const targetAttrs = openInNewTab ? ' target="_blank" rel="noreferrer"' : '';
    wrapInline(`<a href="${safeHref}"${targetAttrs}>`, '</a>', 'link text');
  };

  const insertPageLink = () => {
    if (!pageId) return;

    const safePageId = escapeAttr(pageId);
    wrapInline(`<a href="#pb-page:${safePageId}" data-page-id="${safePageId}">`, '</a>', 'page link');
  };

  const insertTokenAtCursor = (token) => {
    if (!token) return;
    applyInsertion((selected) => selected ? `${selected} ${token}` : token);
  };

  return (
    <div className="pagebuilder-editor-fields">
      <div className="pagebuilder-token-buttons__group" style={{ marginBottom: '0.5rem' }}>
        <span className="pagebuilder-token-buttons__title">Structure</span>
        <button type="button" className="pagebuilder-block-action-btn pagebuilder-token-btn" onMouseDown={(e) => e.preventDefault()} onClick={() => wrapBlock('h1', 'Heading')}><strong>H1</strong></button>
        <button type="button" className="pagebuilder-block-action-btn pagebuilder-token-btn" onMouseDown={(e) => e.preventDefault()} onClick={() => wrapBlock('h2', 'Heading')}><strong>H2</strong></button>
        <button type="button" className="pagebuilder-block-action-btn pagebuilder-token-btn" onMouseDown={(e) => e.preventDefault()} onClick={() => wrapBlock('h3', 'Heading')}><strong>H3</strong></button>
        <button type="button" className="pagebuilder-block-action-btn pagebuilder-token-btn" onMouseDown={(e) => e.preventDefault()} onClick={() => wrapBlock('p', 'Paragraph text')}>P</button>
        <button type="button" className="pagebuilder-block-action-btn pagebuilder-token-btn" onMouseDown={(e) => e.preventDefault()} onClick={() => insertList(false)}><i className="bi bi-list-ul" aria-hidden="true" /></button>
        <button type="button" className="pagebuilder-block-action-btn pagebuilder-token-btn" onMouseDown={(e) => e.preventDefault()} onClick={() => insertList(true)}><i className="bi bi-list-ol" aria-hidden="true" /></button>
      </div>

      <div className="pagebuilder-token-buttons__group" style={{ marginBottom: '0.5rem' }}>
        <span className="pagebuilder-token-buttons__title">Text Style</span>
        <button type="button" className="pagebuilder-block-action-btn pagebuilder-token-btn" onMouseDown={(e) => e.preventDefault()} onClick={() => wrapInline('<strong>', '</strong>', 'bold text')}><strong>B</strong></button>
        <button type="button" className="pagebuilder-block-action-btn pagebuilder-token-btn" onMouseDown={(e) => e.preventDefault()} onClick={() => wrapInline('<em>', '</em>', 'italic text')}><em>I</em></button>
        <button type="button" className="pagebuilder-block-action-btn pagebuilder-token-btn" onMouseDown={(e) => e.preventDefault()} onClick={() => wrapInline('<u>', '</u>', 'underlined text')}><u>U</u></button>
        <button type="button" className="pagebuilder-block-action-btn pagebuilder-token-btn" onMouseDown={(e) => e.preventDefault()} onClick={() => wrapInline('<s>', '</s>', 'strike text')}><s>S</s></button>
      </div>

      <div className="pagebuilder-token-buttons__group" style={{ marginBottom: '0.5rem' }}>
        <span className="pagebuilder-token-buttons__title">Line Alignment</span>
        <button type="button" className="pagebuilder-block-action-btn pagebuilder-token-btn" onMouseDown={(e) => e.preventDefault()} onClick={() => wrapBlock('div', 'Aligned line', 'style="text-align:left;"')}><i className="bi bi-text-left" aria-hidden="true" /></button>
        <button type="button" className="pagebuilder-block-action-btn pagebuilder-token-btn" onMouseDown={(e) => e.preventDefault()} onClick={() => wrapBlock('div', 'Aligned line', 'style="text-align:center;"')}><i className="bi bi-text-center" aria-hidden="true" /></button>
        <button type="button" className="pagebuilder-block-action-btn pagebuilder-token-btn" onMouseDown={(e) => e.preventDefault()} onClick={() => wrapBlock('div', 'Aligned line', 'style="text-align:right;"')}><i className="bi bi-text-right" aria-hidden="true" /></button>
        <button type="button" className="pagebuilder-block-action-btn pagebuilder-token-btn" onMouseDown={(e) => e.preventDefault()} onClick={() => wrapBlock('div', 'Aligned line', 'style="text-align:justify;"')}><i className="bi bi-justify" aria-hidden="true" /></button>
      </div>

      <div className="pagebuilder-field-row mb-2">
        <Form.Group style={{ flex: 1 }}>
          <Form.Label>External Link URL</Form.Label>
          <Form.Control
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            placeholder="https://example.com"
          />
        </Form.Group>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.5rem' }}>
          <Form.Check
            type="checkbox"
            label="Open in new tab"
            checked={openInNewTab}
            onChange={(e) => setOpenInNewTab(e.target.checked)}
          />
          <button
            type="button"
            className="pagebuilder-block-action-btn"
            onMouseDown={(e) => e.preventDefault()}
            onClick={insertExternalLink}
          >
            <i className="bi bi-link-45deg" aria-hidden="true" /> Insert Link
          </button>
        </div>
      </div>

      <div className="pagebuilder-field-row mb-3">
        <Form.Group style={{ flex: 1 }}>
          <Form.Label>Link to Page Builder Page</Form.Label>
          <Form.Select value={pageId} onChange={(e) => setPageId(e.target.value)}>
            <option value="">Select page...</option>
            {pageOptions.map((option) => (
              <option key={option.id} value={option.id}>{option.label}</option>
            ))}
          </Form.Select>
          <Form.Text className="pagebuilder-help-note">
            Inserts an in-app page link that switches to the selected Page Builder tab/page.
          </Form.Text>
        </Form.Group>
        <div style={{ display: 'flex', alignItems: 'flex-end' }}>
          <button
            type="button"
            className="pagebuilder-block-action-btn"
            onMouseDown={(e) => e.preventDefault()}
            onClick={insertPageLink}
            disabled={!pageId}
          >
            <i className="bi bi-diagram-3" aria-hidden="true" /> Insert Page Link
          </button>
        </div>
      </div>

      <Form.Group className="mb-2">
        <Form.Label>Text Section HTML</Form.Label>
        <Form.Control
          as="textarea"
          rows={12}
          ref={textareaRef}
          value={body}
          onChange={(e) => update('body', e.target.value)}
          placeholder="Write rich text using the toolbar above..."
          style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}
        />
      </Form.Group>

      <TokenInsertButtons onInsert={insertTokenAtCursor} />
    </div>
  );
}
